// components/CaseFeedItems.jsx
import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  User,
  Calendar,
  ThumbsUp,
  MessageCircle,
  Link as LinkIcon,
  FileText,
  CheckCircle,
  AlertCircle,
  Info,
  Loader2,
  ChevronDown,
  ChevronUp,
  Paperclip,
  RefreshCw,
  File,
} from "lucide-react";
import apiService from "../../services/api";
import CaseTasks from "./CaseTasks";
import { useTheme } from "../../contexts/ThemeContext";

const CaseFeedItems = ({ caseId }) => {
  const { isDarkMode } = useTheme();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [filterType, setFilterType] = useState("all");
  const [activeTab, setActiveTab] = useState("feed");

  useEffect(() => {
    fetchFeedItems();
  }, [caseId, filterType]);

  const fetchFeedItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filterType !== "all") {
        params.type = filterType;
      }

      const response = await apiService.getCaseFeedItems(caseId, params);
      setFeedItems(response.data || []);
    } catch (err) {
      console.error("Error fetching feed items:", err);
      setError("Failed to load feed items");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleComments = (itemId) => {
    setExpandedComments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getTypeIcon = (type) => {
    const icons = {
      TextPost: MessageSquare,
      ContentPost: FileText,
      LinkPost: LinkIcon,
      EmailMessageEvent: MessageCircle,
      TrackedChange: AlertCircle,
      default: Info,
    };
    const IconComponent = icons[type] || icons.default;
    return <IconComponent className="w-4 h-4" />;
  };

  const getTypeColor = (type) => {
    const colors = {
      TextPost: "text-blue-400 bg-blue-500/10",
      ContentPost: "text-purple-400 bg-purple-500/10",
      LinkPost: "text-green-400 bg-green-500/10",
      EmailMessageEvent: "text-yellow-400 bg-yellow-500/10",
      TrackedChange: "text-orange-400 bg-orange-500/10",
      default: `text-gray-400 ${isDarkMode ? "bg-gray-500/10" : "bg-gray-200"}`,
    };
    return colors[type] || colors.default;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getUserDisplay = (feedItem) => {
    return feedItem.creator || feedItem.salesforceCreator;
  };

  const truncateText = (text, maxLength = 300) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const renderTrackedChanges = (trackedChanges) => {
    if (!trackedChanges || trackedChanges.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <h4
          className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} flex items-center gap-1`}
        >
          <RefreshCw className="w-4 h-4" />
          Field Changes
        </h4>
        <div className="space-y-2">
          {trackedChanges.map((change, idx) => (
            <div
              key={idx}
              className={`${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-100 border-gray-200"} rounded-lg p-3 border`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  {change.fieldName}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-red-400 line-through">
                  {change.oldValue || "(empty)"}
                </span>
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  →
                </span>
                <span className="text-green-400">
                  {change.newValue || "(empty)"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <h4
          className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} flex items-center gap-1`}
        >
          <Paperclip className="w-4 h-4" />
          Attachments
        </h4>
        <div className="space-y-2">
          {attachments.map((attachment, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-2 p-2 ${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-100 border-gray-200"} rounded-lg border`}
            >
              <File className="w-4 h-4 text-blue-400" />
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"} truncate`}
                >
                  {attachment.title || "Untitled Attachment"}
                </p>
                {attachment.type && (
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {attachment.type}
                  </p>
                )}
              </div>
              {attachment.recordId && (
                <span
                  className={`text-xs ${isDarkMode ? "text-gray-500 bg-gray-700" : "text-gray-400 bg-gray-200"} px-2 py-1 rounded`}
                >
                  ID: {attachment.recordId.substring(0, 8)}...
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderComments = (item) => {
    if (!item.comments || item.comments.length === 0) return null;

    const isExpanded = expandedComments.has(item.id);
    const commentsToShow = isExpanded
      ? item.comments
      : item.comments.slice(0, 2);

    return (
      <div
        className={`mt-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-3`}
      >
        <div className="flex items-center justify-between mb-2">
          <h4
            className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} flex items-center gap-1`}
          >
            <MessageCircle className="w-4 h-4" />
            Comments ({item.comments.length})
          </h4>
          {item.comments.length > 2 && (
            <button
              onClick={() => toggleComments(item.id)}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              {isExpanded ? "Show Less" : `Show All ${item.comments.length}`}
            </button>
          )}
        </div>
        <div className="space-y-3">
          {commentsToShow.map((comment) => {
            const commentUser = comment.creator || comment.salesforceCreator;
            return (
              <div
                key={comment.id}
                className={`flex gap-2 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"} rounded-lg p-3`}
              >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                  {commentUser?.profileImageUrl ? (
                    <img
                      src={commentUser.profileImageUrl}
                      alt={commentUser.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    getInitials(commentUser?.name || "Unknown")
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {commentUser?.name || "Unknown User"}
                    </span>
                    <span
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      {formatDate(comment.salesforceCreatedDate)}
                    </span>
                    {comment.isVerified && (
                      <CheckCircle className="w-3 h-3 text-green-400" />
                    )}
                  </div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"} whitespace-pre-wrap`}
                  >
                    {comment.commentBody}
                  </p>
                  {comment.commentType && (
                    <span
                      className={`inline-block mt-1 text-xs ${isDarkMode ? "text-gray-500 bg-gray-700" : "text-gray-400 bg-gray-200"} px-2 py-0.5 rounded`}
                    >
                      {comment.commentType}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2
          className={`w-8 h-8 ${isDarkMode ? "text-blue-500" : "text-blue-600"} animate-spin`}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        <AlertCircle className="w-12 h-12 mb-3 text-red-500" />
        <p>{error}</p>
        <button
          onClick={fetchFeedItems}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className={`flex items-center gap-2 mb-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <button
          onClick={() => setActiveTab("feed")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "feed"
              ? isDarkMode
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-blue-600 border-b-2 border-blue-600"
              : isDarkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Feed Items
        </button>
        <button
          onClick={() => setActiveTab("tasks")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "tasks"
              ? isDarkMode
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-blue-600 border-b-2 border-blue-600"
              : isDarkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Tasks
        </button>
      </div>

      {activeTab === "feed" ? (
        <>
          {/* Filter Bar */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setFilterType("all")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterType === "all"
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType("TextPost")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterType === "TextPost"
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Posts
            </button>
            <button
              onClick={() => setFilterType("EmailMessageEvent")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterType === "EmailMessageEvent"
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Emails
            </button>
            <button
              onClick={() => setFilterType("TrackedChange")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterType === "TrackedChange"
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Changes
            </button>
          </div>

          {/* Feed Items List */}
          {feedItems.length === 0 ? (
            <div
              className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <MessageSquare
                className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
              />
              <p>No feed items found</p>
              <p className="text-sm mt-1">
                {filterType !== "all"
                  ? "Try changing the filter"
                  : "No activity has been synced from Salesforce yet"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {feedItems.map((item) => {
                const user = getUserDisplay(item);
                const isExpanded = expandedItems.has(item.id);
                const shouldTruncate = item.body && item.body.length > 300;

                return (
                  <div
                    key={item.id}
                    className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"} border rounded-lg p-4 transition-colors`}
                  >
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      {/* User Avatar */}
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                        {user?.profileImageUrl ? (
                          <img
                            src={user.profileImageUrl}
                            alt={user.name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          getInitials(user?.name || "Unknown")
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* User Info & Timestamp */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`${isDarkMode ? "text-white" : "text-gray-900"} font-medium`}
                            >
                              {user?.name || "Unknown User"}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${getTypeColor(
                                item.type,
                              )}`}
                            >
                              {getTypeIcon(item.type)}
                              {item.type?.replace(/([A-Z])/g, " $1").trim() ||
                                "Post"}
                            </span>
                          </div>
                          <span
                            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {formatDate(item.salesforceCreatedDate)}
                          </span>
                        </div>

                        {/* Title */}
                        {item.title && (
                          <h3
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} font-medium mb-2`}
                          >
                            {item.title}
                          </h3>
                        )}

                        {/* Body */}
                        {item.body && (
                          <div
                            className={`${isDarkMode ? "text-gray-300" : "text-gray-600"} mb-3`}
                          >
                            <p className="whitespace-pre-wrap">
                              {isExpanded || !shouldTruncate
                                ? item.body
                                : truncateText(item.body)}
                            </p>
                            {shouldTruncate && (
                              <button
                                onClick={() => toggleExpanded(item.id)}
                                className="text-blue-400 hover:text-blue-300 text-sm mt-2 flex items-center gap-1"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp className="w-4 h-4" />
                                    Show Less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-4 h-4" />
                                    Show More
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Link */}
                        {item.hasLink && item.linkUrl && (
                          <a
                            href={item.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm mb-3 p-2 bg-blue-500/10 rounded-lg w-fit"
                          >
                            <LinkIcon className="w-4 h-4" />
                            <span className="truncate">{item.linkUrl}</span>
                          </a>
                        )}

                        {/* Tracked Changes */}
                        {renderTrackedChanges(item.trackedChanges)}

                        {/* Attachments */}
                        {renderAttachments(item.attachments)}

                        {/* Engagement Stats */}
                        <div
                          className={`flex items-center gap-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-3`}
                        >
                          {item.likeCount > 0 && (
                            <div className="flex items-center gap-1">
                              <ThumbsUp className="w-4 h-4" />
                              <span>{item.likeCount}</span>
                            </div>
                          )}
                          {item.commentCount > 0 && (
                            <div className="flex items-center gap-1">
                              <MessageCircle className="w-4 h-4" />
                              <span>{item.commentCount}</span>
                            </div>
                          )}
                          {item.hasContent && (
                            <div className="flex items-center gap-1 text-purple-400">
                              <FileText className="w-4 h-4" />
                              <span>Has Content</span>
                            </div>
                          )}
                          {item.hasVerifiedComment && (
                            <div className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="w-4 h-4" />
                              <span>Verified</span>
                            </div>
                          )}
                        </div>

                        {/* Status Indicators */}
                        {(item.isClosed || item.status) && (
                          <div className="flex items-center gap-2 mt-2">
                            {item.isClosed && (
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? "bg-gray-500/20 text-gray-400" : "bg-gray-200 text-gray-600"}`}
                              >
                                Closed
                              </span>
                            )}
                            {item.status && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                                {item.status}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Comments Section */}
                        {renderComments(item)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <CaseTasks caseId={caseId} />
      )}
    </div>
  );
};

export default CaseFeedItems;
